import React, { useState, useEffect, useCallback } from 'react';
import { fetchAuth } from '../../utils/api';
import '../../styles/components.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function PurchaseHistoryView({ user, handleApiError, showNotification }) {
    const [purchaseBills, setPurchaseBills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedBillId, setExpandedBillId] = useState(null);
    const [billItems, setBillItems] = useState({});
    const [loadingItems, setLoadingItems] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const fetchPurchaseBills = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchAuth(`${API_URL}/api/purchases`);
            if (res.ok) setPurchaseBills(await res.json());
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    }, [handleApiError]);

    useEffect(() => {
        fetchPurchaseBills();
    }, [fetchPurchaseBills]);

    const handleExpandBill = async (billId) => {
        if (expandedBillId === billId) {
            setExpandedBillId(null);
            return;
        }

        setExpandedBillId(billId);
        if (!billItems[billId]) {
            setLoadingItems(true);
            try {
                const res = await fetchAuth(`${API_URL}/api/purchases/${billId}`);
                if (res.ok) {
                    const items = await res.json();
                    setBillItems(prev => ({ ...prev, [billId]: items }));
                }
            } catch (error) {
                handleApiError(error);
            } finally {
                setLoadingItems(false);
            }
        }
    };

    const handleDeleteBill = async (billId) => {
        try {
            const res = await fetchAuth(`${API_URL}/api/purchases/${billId}`, { method: 'DELETE' });
            if (res.ok) {
                showNotification('Purchase bill deleted and stock reversed.', 'success');
                setDeleteConfirm(null);
                setExpandedBillId(null);
                fetchPurchaseBills();
            } else {
                const err = await res.json();
                throw new Error(err.message || 'Failed to delete');
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    if (loading) return <div>Loading purchase history...</div>;

    return (
        <div className="page-container">
            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '420px' }}>
                        <h3 style={{ margin: '0 0 12px', color: '#ef4444' }}>⚠️ Delete Purchase Bill</h3>
                        <p style={{ margin: '0 0 8px', color: '#475569' }}>
                            Are you sure you want to delete this purchase from <strong>{deleteConfirm.supplierName || 'Unknown'}</strong>?
                        </p>
                        <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#94a3b8' }}>
                            This will reverse all stock changes from this bill. This action cannot be undone.
                        </p>
                        <div className="modal-actions">
                            <button type="button" className="cancel-btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                            <button type="button" className="confirm-btn" style={{ background: '#ef4444' }} onClick={() => handleDeleteBill(deleteConfirm.id)}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="page-header">
                <h2 className="page-title">Purchase History</h2>
            </div>
            <div className="data-display">
                <h3>All Purchase Bills</h3>
                <div className="inventory-table-container">
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Bill Number</th>
                                <th>Supplier</th>
                                <th>Total Amount</th>
                                {user.role === 'Admin' && <th style={{ width: '60px' }}>Action</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {purchaseBills.map(bill => (
                                <React.Fragment key={bill.id}>
                                    <tr onClick={() => handleExpandBill(bill.id)} style={{ cursor: 'pointer', backgroundColor: expandedBillId === bill.id ? 'var(--color-bg-light)' : 'transparent' }}>
                                        <td>{new Date(bill.purchaseDate).toLocaleDateString('en-IN')}</td>
                                        <td>{bill.billNumber || 'N/A'}</td>
                                        <td>{bill.supplierName}</td>
                                        <td>₹{parseFloat(bill.totalAmount).toFixed(2)}</td>
                                        {user.role === 'Admin' && (
                                            <td>
                                                <button
                                                    className="btn-icon"
                                                    title="Delete"
                                                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(bill); }}
                                                    style={{ color: '#ef4444', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px' }}
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path></svg>
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                    {expandedBillId === bill.id && (
                                        <tr className="expanded-row">
                                            <td colSpan={user.role === 'Admin' ? "5" : "4"} style={{ padding: '16px', backgroundColor: 'var(--color-bg-light)' }}>
                                                {loadingItems && !billItems[bill.id] ? (
                                                    <p>Loading items...</p>
                                                ) : billItems[bill.id] && billItems[bill.id].length > 0 ? (
                                                    <table className="inventory-table" style={{ marginTop: '8px', border: '1px solid var(--color-border)' }}>
                                                        <thead>
                                                            <tr>
                                                                <th style={{ backgroundColor: 'var(--color-surface)' }}>Item Name</th>
                                                                <th style={{ backgroundColor: 'var(--color-surface)' }}>Quantity</th>
                                                                <th style={{ backgroundColor: 'var(--color-surface)' }}>Purchase Price</th>
                                                                <th style={{ backgroundColor: 'var(--color-surface)' }}>Total Price</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {billItems[bill.id].map(item => (
                                                                <tr key={item.id}>
                                                                    <td>{item.nameAtPurchase || item.masterItemName}</td>
                                                                    <td>{item.quantity}</td>
                                                                    <td>₹{parseFloat(item.purchasePrice).toFixed(2)}</td>
                                                                    <td>₹{(parseFloat(item.purchasePrice) * item.quantity).toFixed(2)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <p>No items found for this bill.</p>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default PurchaseHistoryView;
